import type { IItem, TFormMethods } from "@fluidattacks/design";
import {
  ComboBox,
  Container,
  Editable,
  GridContainer,
  Text,
  useWatch,
} from "@fluidattacks/design";
import map from "lodash/map";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { IFormValues } from "../severity-info/types";
import type { GetFindingSeverityQueryQuery } from "gql/graphql";
import { castFieldsCVSS4 } from "pages/finding/severity//cvss4/utils";
import { castFieldsCVSS3 } from "pages/finding/severity/utils";
import { getCVSS31Values } from "utils/cvss";
import { getCVSS40Values } from "utils/cvss4";

interface ISeverityForm {
  control: TFormMethods<IFormValues>["control"];
  data: GetFindingSeverityQueryQuery;
}

const SeverityForm = ({
  control,
  data,
}: Readonly<ISeverityForm>): JSX.Element => {
  const { t } = useTranslation();
  const CVSS_VERSION = "3.1";

  const cvss31Values = getCVSS31Values(data.finding.severityVector);
  const cvss40Values = getCVSS40Values(data.finding.severityVectorV4);

  const [watchedValues, setWatchedValues] = useState<
    Record<string, string | undefined>
  >({});

  const formWatchedValues = useWatch({ control });

  useEffect((): void => {
    setWatchedValues(formWatchedValues as Record<string, string | undefined>);
  }, [formWatchedValues]);

  return (
    <Container>
      <GridContainer lg={2} md={2} sm={2} xl={2}>
        <Container>
          <Text fontWeight={"bold"} mb={1} size={"md"}>
            {`${t("searchFindings.tabSeverity.cvssVersion")}: 4.0`}
          </Text>
          {castFieldsCVSS4(cvss40Values).map((field): JSX.Element => {
            const options = map(field.options, (text, value): IItem => {
              const item = {
                name: t(text),
                value,
              };

              return item;
            });
            const watchedValue =
              watchedValues[`scoreV4${field.name}`] ?? field.currentValue;

            return (
              <Container key={field.name}>
                <Editable
                  currentValue={`${watchedValue} | ${t(field.options[watchedValue])}`}
                  isEditing={true}
                  label={field.title}
                  name={`scoreV4${field.name}`}
                >
                  <ComboBox
                    control={control}
                    id={`scoreV4${field.name}`}
                    items={options}
                    label={field.title}
                    name={`scoreV4${field.name}`}
                  />
                </Editable>
              </Container>
            );
          })}
        </Container>
        <Container>
          <Text fontWeight={"bold"} mb={1} size={"md"}>
            {`${t("searchFindings.tabSeverity.cvssVersion")}: ${CVSS_VERSION}`}
          </Text>
          {castFieldsCVSS3(cvss31Values).map((field): JSX.Element => {
            const currentOptionKey = field.options[field.currentValue];
            const currentOption = t(currentOptionKey);
            const options = map(field.options, (text, value): IItem => {
              const item = {
                name: t(text),
                value,
              };

              return item;
            });

            return (
              <Container key={field.name}>
                <Editable
                  currentValue={`${field.currentValue} | ${t(currentOption)}`}
                  isEditing={true}
                  label={field.name}
                  name={field.name}
                >
                  <ComboBox
                    control={control}
                    id={field.name}
                    items={options}
                    label={field.title}
                    name={field.name}
                  />
                </Editable>
              </Container>
            );
          })}
        </Container>
      </GridContainer>
    </Container>
  );
};

export { SeverityForm };
